import { Heading } from "@/components/heading/Heading";
import { HeadingSection } from "@/components/heading/HeadingSection";
import { ManageTeamModal } from "@/features/team/manageTeam/ManageTeamModal";
import { type CalendarSettings, type CalendarView, useTeam } from "@repo/api";
import { ListView, ListViewItem, RadioButtonGroup } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ElementsContainer,
  ModalSheet,
  RegionContainer,
  SectionContainer,
  useSettings,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { useState } from "react";

import { UploadScheduleModal } from "../calendar/team-calendar/UploadScheduleModal";
import { isUploadScheduleEnabled } from "../calendar/utils/utils";

interface CalendarSettingsModalProps {
  isOpen: boolean;
  closeModal: () => void;
  settings: CalendarSettings;
  selectedTeam?: number;
  onChangeSettings: (settings: CalendarSettings) => void;
  onUpdateTeams?: () => void;
  tableView?: CalendarView;
}

export const CalendarSettingsModal = ({
  isOpen,
  closeModal,
  settings,
  selectedTeam,
  onChangeSettings,
  onUpdateTeams,
  tableView,
}: CalendarSettingsModalProps) => {
  const { calendar } = useSettings();

  const { data: team } = useTeam(selectedTeam);
  const [showManageTeam, setShowManageTeam] = useState(false);
  const [showUploadSchedule, setShowUploadSchedule] = useState(false);

  const handleChangeSettings = (newSettings: CalendarSettings) => {
    onChangeSettings(newSettings);
  };

  const showUploadScheduleSetting = isUploadScheduleEnabled(
    calendar,
    team?.buCode
  );

  return (
    <>
      <ModalSheet
        title={t("label.calendar_settings")}
        visible={isOpen}
        onClose={closeModal}
      >
        <HeadingSection value={3}>
          <RegionContainer>
            <SectionContainer>
              <ElementsContainer>
                <Heading textStyle="Heading.S">{t("label.settings")}</Heading>
                <ListView id="calendar-settings-list">
                  <ListViewItem
                    title={t("action.manage_team")}
                    control="navigational"
                    onClick={() => {
                      setShowManageTeam(true);
                      trackAnalytics("manage_team");
                    }}
                  />
                  {tableView === "TEAM" && showUploadScheduleSetting && (
                    <ListViewItem
                      title={t("action.upload_schedule")}
                      control="navigational"
                      onClick={() => {
                        setShowUploadSchedule(true);
                      }}
                    />
                  )}
                </ListView>
              </ElementsContainer>

              <div></div>
              <Heading textStyle="Heading.S">
                {t("label.customise_calendar")}
              </Heading>
              <ListView
                id="calendar-customize-list"
                showDivider={false}
                size="small"
              >
                <ListViewItem
                  title={t("label.outlook_calendar")}
                  controlProps={{
                    value: "showOutlookCalendar",
                    checked: settings.showOutlookCalendar,
                  }}
                  control="switch"
                  onChange={(event) => {
                    const newSettings = {
                      ...settings,
                      showOutlookCalendar: event.currentTarget.checked,
                    };
                    handleChangeSettings(newSettings);
                    trackAnalytics("show-outlook-calendar");
                  }}
                />
                <ListViewItem
                  title={t("action.show_week_number")}
                  controlProps={{
                    value: "showWeekNumber",
                    checked: settings.showWeekNumber,
                  }}
                  control="switch"
                  onChange={(event) => {
                    const newSettings = {
                      ...settings,
                      showWeekNumber: event.currentTarget.checked,
                    };
                    handleChangeSettings(newSettings);
                    trackAnalytics("show-week-number");
                  }}
                />
              </ListView>
              <RadioButtonGroup
                name={t("label.start_week_on")}
                subtle
                list={[
                  {
                    id: "sunday",
                    name: t("label.start_week_on"),
                    label: t("label.sunday"),
                    value: "sunday",
                    checked: settings.firstDayOfWeek === "sunday",
                    onChange: () => {
                      const newSettings = {
                        ...settings,
                        firstDayOfWeek: "sunday" as const,
                      };
                      handleChangeSettings(newSettings);
                      trackAnalytics("start_week_on");
                    },
                  },
                  {
                    id: "monday",
                    name: t("label.start_week_on"),
                    label: t("label.monday"),
                    value: "monday",
                    checked: settings.firstDayOfWeek === "monday",
                    onChange: () => {
                      const newSettings = {
                        ...settings,
                        firstDayOfWeek: "monday" as const,
                      };
                      handleChangeSettings(newSettings);
                      trackAnalytics("start_week_on");
                    },
                  },
                ]}
              />
              <RadioButtonGroup
                name={t("label.preferred_calendar_view")}
                subtle
                list={[
                  {
                    id: "AGENDA",
                    name: t("label.preferred_calendar_view"),
                    label: t("label.agenda"),
                    value: "AGENDA",
                    checked: settings.preferredCalendarView === "AGENDA",
                    onChange: () => {
                      const newSettings = {
                        ...settings,
                        preferredCalendarView: "AGENDA" as const,
                      };
                      handleChangeSettings(newSettings);
                      trackAnalytics("preferred_calendar_view");
                    },
                  },
                  {
                    id: "OVERVIEW",
                    name: t("label.preferred_calendar_view"),
                    label: t("label.overview"),
                    value: "OVERVIEW",
                    checked: settings.preferredCalendarView === "OVERVIEW",
                    onChange: () => {
                      const newSettings = {
                        ...settings,
                        preferredCalendarView: "OVERVIEW" as const,
                      };
                      handleChangeSettings(newSettings);
                      trackAnalytics("preferred_calendar_view");
                    },
                  },
                  {
                    id: "TEAM",
                    name: t("label.preferred_calendar_view"),
                    label: t("label.team"),
                    value: "TEAM",
                    checked: settings.preferredCalendarView === "TEAM",
                    onChange: () => {
                      const newSettings = {
                        ...settings,
                        preferredCalendarView: "TEAM" as const,
                      };
                      handleChangeSettings(newSettings);
                      trackAnalytics("preferred_calendar_view");
                    },
                  },
                ]}
              />
            </SectionContainer>
          </RegionContainer>
        </HeadingSection>
      </ModalSheet>

      <ManageTeamModal
        isOpen={showManageTeam}
        closeModal={() => setShowManageTeam(false)}
        onUpdate={onUpdateTeams}
      />

      <UploadScheduleModal
        isOpen={showUploadSchedule}
        closeModal={() => setShowUploadSchedule(false)}
        selectedTeam={selectedTeam}
      />
    </>
  );
};
